// eslint-disable-next-line react-hooks/exhaustive-deps
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useLayoutEffect, useRef, useCallback, useEffect, useMemo, memo } from 'react';
import PropTypes from 'prop-types';

import { workReportLineStatus } from '../../../../utils/dataStatus';
import './InputUnitMeasure.scss';
import ComponentWithTooltip from '../../../ComponentWithTooltip/ComponentWithTooltip';
import colors from '../../../../styles/colors.module.scss';
import { validateDecimalInput } from '../../../../utils/formatUtils';

const getBackgroundStyle = (rowData, forceWindow, backgroundColor, backgroundColorForceWindow) => {
  if (rowData?.isOutWindowNotification) {
    return forceWindow
      ? `linear-gradient(to left, rgb(209, 82, 82) 5%, ${backgroundColorForceWindow} 5%, ${backgroundColorForceWindow})`
      : `linear-gradient(to left, #d15252 5%, ${backgroundColor} 5% 75%)`;
  }

  return null;
};

const isvalidateForViewTooltop = (rowData) =>
  rowData?.isOutWindowNotification || rowData?.disabledBySap;

const handleBackgroundColor = (rowData, backgroundColor) =>
  !rowData?.isOutWindowNotification ? backgroundColor : null;

const handlePlaceholder = (rowData) => (rowData?.unitMeasureValue ? '-' : '');

const getColors = (rowData, initalRowData, isValueChanged, isStateChanged) => {
  // eslint-disable-next-line no-restricted-globals
  const isNanValue = isNaN(parseFloat(rowData?.unitMeasureValue, 10));

  if (isValueChanged && !isNanValue) {
    return [colors.dark, colors.white];
  }
  if (!isValueChanged && !isNanValue) {
    return [initalRowData?.colors.textColor, initalRowData?.colors.backgroundColor];
  }
  if (isStateChanged) {
    return [rowData?.colors.textColor, rowData?.colors.backgroundColor];
  }

  return ['', ''];
};

const checkValueChanged = (rowData, initalRowData) =>
  parseFloat(rowData?.unitMeasureValue, 10) !== parseFloat(initalRowData?.unitMeasureValue, 10);

const checkStateChanged = (rowData, initalRowData) =>
  initalRowData?.stateOfUnitMeasure !== rowData?.stateOfUnitMeasure;

const updateColors = (rowData, initalRowData, setColor, setBackgroundColor) => {
  const isValueChanged = checkValueChanged(rowData, initalRowData);
  const isStateChanged = checkStateChanged(rowData, initalRowData);

  const [newColor, newBackgroundColor] = getColors(
    rowData,
    initalRowData,
    isValueChanged,
    isStateChanged
  );

  if (newColor !== '') {
    setColor(newColor);
  }
  if (newBackgroundColor !== '') {
    setBackgroundColor(newBackgroundColor);
  }
};

const getTooltipText = (rowData) => {
  if (rowData.disabledBySap) {
    return 'Inactiva por SAP';
  }
  if (rowData.isOutWindowNotification) {
    return 'Fecha fuera de la ventana de notificación';
  }

  return '';
};

const initializeColors = (rowData, forceWindow, colorsValue) => {
  const textColor = rowData?.colors?.textColor ?? colorsValue.dark;
  const bgColor = rowData?.colors?.backgroundColor ?? colorsValue.lightGray;
  const bgColorForceWindow = forceWindow ? colorsValue.lightGray : '';

  return { textColor, bgColor, bgColorForceWindow };
};

const noChange = 0;
const InputUnitMeasure = ({ rowData, setRowData, focus, forceWindow }) => {
  const { textColor, bgColor, bgColorForceWindow } = initializeColors(rowData, forceWindow, colors);
  const [color, setColor] = useState(textColor);
  const [backgroundColor, setBackgroundColor] = useState(bgColor);
  const [backgroundColorForceWindow] = useState(bgColorForceWindow);
  const inputElement = useRef();
  const stateOfUnitMeasure = rowData?.stateOfUnitMeasure;
  const initalRowData = useMemo(() => rowData, [noChange]);

  const backgroundStyle = getBackgroundStyle(
    rowData,
    forceWindow,
    backgroundColor,
    backgroundColorForceWindow
  );

  const createNewRowData = useCallback(
    (number) => ({
      ...initalRowData,
      colors: {
        backgroundColor,
        textColor: color
      },
      unitMeasureValue: number,
      stateOfUnitMeasure
    }),
    [backgroundColor, color, initalRowData, stateOfUnitMeasure]
  );

  const handleChange = (evt) => {
    if (validateDecimalInput(evt.target.value, 5)) {
      setRowData(createNewRowData(parseFloat(evt.target.value)));
    }
  };

  const handleKeyPress = (evt) => {
    const regExpression = /[0-9]*\.?[0-9]*/;

    // eslint-disable-next-line no-unsafe-optional-chaining
    if (!regExpression.test(rowData?.unitMeasureValue + evt.key)) {
      evt.preventDefault();
    }
  };

  useLayoutEffect(() => {
    if (focus) {
      inputElement.current?.focus();
    } else {
      if (
        rowData?.stateOfUnitMeasure === workReportLineStatus.devolver &&
        !rowData?.unitMeasureValue
      ) {
        setRowData(createNewRowData(initalRowData?.unitMeasureValue));
      }

      inputElement.current?.blur();
    }
  }, [focus]);

  useEffect(() => {
    updateColors(rowData, initalRowData, setColor, setBackgroundColor);
  }, [rowData]);
  const inputComponent = () => (
    <input
      className="input-unit-measure"
      disabled={rowData?.disabled || rowData?.disabledBySap}
      onChange={handleChange}
      onKeyPress={handleKeyPress}
      placeholder={handlePlaceholder(rowData)}
      ref={inputElement}
      style={{
        color,
        // eslint-disable-next-line no-nested-ternary
        background: backgroundStyle,
        backgroundColor: handleBackgroundColor(rowData, backgroundColor)
      }}
      type="number"
      value={rowData?.unitMeasureValue ?? undefined}
    />
  );

  return isvalidateForViewTooltop(rowData) ? (
    <ComponentWithTooltip tooltipText={getTooltipText(rowData)}>
      {inputComponent()}
    </ComponentWithTooltip>
  ) : (
    inputComponent()
  );
};

InputUnitMeasure.propTypes = {
  rowData: PropTypes.shape({
    isOutWindowNotification: PropTypes.bool,
    colors: PropTypes.shape({
      backgroundColor: PropTypes.string,
      textColor: PropTypes.string
    }),
    unitMeasureValue: PropTypes.number,
    stateOfUnitMeasure: PropTypes.number,
    disabled: PropTypes.bool,
    disabledBySap: PropTypes.bool
  }),
  setRowData: PropTypes.func,
  focus: PropTypes.bool.isRequired,
  forceWindow: PropTypes.bool.isRequired
};

InputUnitMeasure.defaultProps = {
  setRowData: () => {},
  rowData: undefined
};

export default memo(InputUnitMeasure);
